import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { qualificationApi, STAGE_SUGGESTION_LABELS } from '../qualificationApi';
import { losApi, LOAN_STATUS_LABELS } from '../losApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import PickSelect from '../shared/PickSelect';

const PERCENT_FIELDS = new Set(['ltv', 'dti', 'interest_rate', 'rate']);
const fmtCell = (field, v) => {
  if (typeof v !== 'number') return String(v ?? '—');
  if (PERCENT_FIELDS.has(field)) return `${v.toFixed(2)}%`;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

// S4 (ticket 130): apply-to-loan / create-loan / pre-approval letter actions
// for a single program card. `loanId` (from the page) or `scenario.loan_id`
// (from the scenario itself) pin the target loan file; otherwise we look up
// the contact's loan files and let the user pick.
export default function ScenarioActions({ scenario, program, loanId, contactId }) {
  const [busy, setBusy] = useState(false);
  const [diff, setDiff] = useState(null); // { rows, targetLoanId }
  const [candidateLoans, setCandidateLoans] = useState(null); // array shown when several loans match
  const [pickedLoanId, setPickedLoanId] = useState('');
  const [banner, setBanner] = useState(null); // { stageSuggestion, loanFileId }
  const [letterUrl, setLetterUrl] = useState(null);

  if (!scenario || program.status === 'not_qualified') return null;

  const knownLoanId = loanId || scenario.loan_id || null;

  const resolveLoanId = async () => {
    if (knownLoanId) return knownLoanId;
    if (pickedLoanId) return pickedLoanId;
    const loans = await losApi.listLoans({ contact_id: contactId });
    const list = Array.isArray(loans) ? loans : [];
    if (list.length === 0) {
      toast({ title: 'No loan file for this contact yet — use Create loan' });
      return null;
    }
    if (list.length === 1) return list[0].loan_file_id;
    setCandidateLoans(list);
    return null;
  };

  const startApply = async () => {
    const targetLoanId = await resolveLoanId();
    if (!targetLoanId) return;
    setBusy(true);
    try {
      const res = await qualificationApi.applyToLoan(scenario.scenario_id, {
        loan_id: targetLoanId,
        program_code: program.code,
        confirm: false,
      });
      setDiff({ rows: res.diff || [], targetLoanId });
    } catch (e) {
      toast({ title: 'Could not apply scenario', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const pickLoanAndApply = async () => {
    if (!pickedLoanId) return;
    setCandidateLoans(null);
    setBusy(true);
    try {
      const res = await qualificationApi.applyToLoan(scenario.scenario_id, {
        loan_id: pickedLoanId,
        program_code: program.code,
        confirm: false,
      });
      setDiff({ rows: res.diff || [], targetLoanId: pickedLoanId });
    } catch (e) {
      toast({ title: 'Could not apply scenario', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const confirmApply = async () => {
    if (!diff) return;
    setBusy(true);
    try {
      const res = await qualificationApi.applyToLoan(scenario.scenario_id, {
        loan_id: diff.targetLoanId,
        program_code: program.code,
        confirm: true,
      });
      setDiff(null);
      toast({ title: 'Loan updated' });
      setBanner({ stageSuggestion: res.stage_suggestion, loanFileId: res.loan?.loan_file_id || diff.targetLoanId });
    } catch (e) {
      toast({ title: 'Could not apply scenario', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const createLoan = async () => {
    setBusy(true);
    try {
      const res = await qualificationApi.createLoan(scenario.scenario_id, { program_code: program.code });
      toast({ title: 'Loan file created' });
      setBanner({ stageSuggestion: res.stage_suggestion, loanFileId: res.loan?.loan_file_id });
    } catch (e) {
      toast({ title: 'Could not create loan', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const generateLetter = async () => {
    const targetLoanId = knownLoanId || banner?.loanFileId;
    if (!targetLoanId) return;
    setBusy(true);
    try {
      const res = await losApi.generatePreApproval(targetLoanId);
      const url = losApi.publicPdfUrl(res.public_token);
      setLetterUrl(url);
      toast({
        title: 'Pre-approval letter generated',
        description: <a href={url} target="_blank" rel="noreferrer">Open PDF</a>,
      });
    } catch (e) {
      toast({ title: 'Could not generate pre-approval letter', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const letterLoanId = knownLoanId || banner?.loanFileId;

  return (
    <div className="mt-3 pt-2 border-t border-slate-100 space-y-2">
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={startApply} disabled={busy}
          className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
          data-testid={`apply-to-loan-${program.code}`}>
          Apply to loan
        </button>
        <button type="button" onClick={createLoan} disabled={busy}
          className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
          data-testid={`create-loan-${program.code}`}>
          Create loan
        </button>
        {letterLoanId && (
          <button type="button" onClick={generateLetter} disabled={busy}
            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            data-testid={`generate-letter-${program.code}`}>
            Pre-approval letter
          </button>
        )}
      </div>

      {candidateLoans && (
        <div className="flex items-center gap-2 text-xs">
          <div className="w-64">
            <PickSelect
              testId="apply-loan-select"
              value={pickedLoanId}
              onChange={setPickedLoanId}
              placeholder="Select a loan file…"
              options={candidateLoans.map((l) => ({
                value: l.loan_file_id,
                label: `${l.loan_file_id} · ${LOAN_STATUS_LABELS[l.status] || l.status}`,
              }))}
            />
          </div>
          <button type="button" onClick={pickLoanAndApply} disabled={!pickedLoanId || busy}
            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">
            Continue
          </button>
        </div>
      )}

      {banner && (
        <div className="text-xs text-slate-600" data-testid="stage-suggestion-banner">
          {banner.stageSuggestion
            ? `Scenario suggests stage ${STAGE_SUGGESTION_LABELS[banner.stageSuggestion] || banner.stageSuggestion}. Update it on the loan file.`
            : 'Loan updated.'}
          {' '}
          <Link to={`/portal/loans/${banner.loanFileId}`} className="underline" data-testid="stage-suggestion-link">
            Open loan file
          </Link>
        </div>
      )}

      {letterUrl && (
        <div className="text-xs">
          <a href={letterUrl} target="_blank" rel="noreferrer" data-testid="letter-link" className="underline text-slate-600">
            Open pre-approval letter
          </a>
        </div>
      )}

      <Dialog open={!!diff} onOpenChange={(open) => { if (!open) setDiff(null); }}>
        <DialogContent data-testid="apply-diff-modal">
          <DialogHeader>
            <DialogTitle>Apply scenario to loan</DialogTitle>
          </DialogHeader>
          {diff && diff.rows.length === 0 && (
            <p className="text-sm text-slate-500">No changes — the loan already matches this scenario.</p>
          )}
          {diff && diff.rows.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400">
                  <th className="pb-1">Field</th>
                  <th className="pb-1">Current</th>
                  <th className="pb-1">Proposed</th>
                </tr>
              </thead>
              <tbody>
                {diff.rows.map((row) => (
                  <tr key={row.field} data-testid={`apply-diff-row-${row.field}`}>
                    <td className="py-1 pr-2 font-medium text-slate-700">{row.field}</td>
                    <td className="py-1 pr-2 text-slate-500">{fmtCell(row.field, row.current)}</td>
                    <td className="py-1 text-slate-700">{fmtCell(row.field, row.proposed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter>
            <button type="button" onClick={() => setDiff(null)} disabled={busy}
              className="text-xs px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50" data-testid="apply-cancel">
              Cancel
            </button>
            <button type="button" onClick={confirmApply} disabled={busy}
              className="btn-gold text-xs px-3 py-1.5" data-testid="apply-confirm">
              Apply changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
